import { Typography, Box, Grid, useTheme } from '@mui/material';
import { TextFieldWithHelp } from './GeneralTab/components/TextFieldWithHelp';
import { memo, useMemo, useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../contexts/AuthContext';
import {
  I18nTabProps,
  getI18nValue,
  updateI18nValue,
} from '../utils/i18nHelpers';
import { hasExtension } from '../../../utils/branding';
import { useBranding } from '../../../hooks/api/useBranding';
import { RichTextEditorField } from '../../../components/inputs/RichTextEditorField';

const SKIP_KEYS = ['publishing_models', 'payments', 'licenses'];
const HARDCODED_KEYS = new Set([
  'search_title',
  'instructions',
  'info_box',
  'no_results',
  'additional_info',
  'search_placeholder',
]);

const getRichTextFields = (t: any) => [
  {
    key: 'search_title',
    label: t('branding.textFields.search_title.label'),
    description: t('branding.textFields.search_title.description'),
    placeholder: t('branding.textFields.search_title.placeholder'),
    type: 'rich' as const,
    category: 'search' as const,
  },
  {
    key: 'search_placeholder',
    label: t('branding.textFields.search_placeholder.label'),
    description: t('branding.textFields.search_placeholder.description'),
    placeholder: '',
    type: 'text' as const,
    category: 'search' as const,
  },
  {
    key: 'info_box',
    label: t('branding.textFields.info_box.label'),
    description: t('branding.textFields.info_box.description'),
    placeholder: t('branding.textFields.info_box.placeholder'),
    type: 'rich' as const,
    category: 'content' as const,
  },
  {
    key: 'instructions',
    label: t('branding.textFields.instructions.label'),
    description: t('branding.textFields.instructions.description'),
    placeholder: t('branding.textFields.instructions.placeholder'),
    type: 'rich' as const,
    category: 'search' as const,
  },
  {
    key: 'no_results',
    label: t('branding.textFields.no_results.label'),
    description: t('branding.textFields.no_results.description'),
    placeholder: t('branding.textFields.no_results.placeholder'),
    type: 'rich' as const,
    category: 'search' as const,
  },
  {
    key: 'additional_info',
    label: t('branding.textFields.additional_info.label'),
    description: t('branding.textFields.additional_info.description'),
    placeholder: t('branding.textFields.additional_info.placeholder'),
    type: 'rich' as const,
    category: 'content' as const,
  },
];

interface TextFieldConfig {
  key: string;
  label: string;
  description: string;
  placeholder: string;
  type: 'text' | 'rich';
  category: 'search' | 'content' | 'navigation' | 'dynamic';
}

const formatKeyToLabel = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const normalizeValue = (value: string): string => {
  if (!value || value === '<p></p>' || value.trim() === '') return '';
  return value;
};

const TextFieldRenderer = memo(
  ({
    field,
    value: externalValue,
    onChange,
    disabled,
    error,
    isFirst = false,
    lang,
  }: {
    field: TextFieldConfig;
    value: string;
    onChange: (value: string) => void;
    disabled: boolean;
    error?: string[];
    isFirst?: boolean;
    lang?: string;
  }) => {
    const [localValue, setLocalValue] = useState(externalValue);

    useEffect(() => {
      setLocalValue(externalValue);
    }, [externalValue]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalValue(e.target.value);
        onChange(e.target.value);
      },
      [onChange]
    );

    const handleReset = useCallback(() => {
      setLocalValue('');
      onChange('');
    }, [onChange]);

    return (
      <Box
        sx={{
          mt: isFirst ? '0px' : field.description ? '40px' : '16px',
          mb: '16px',
        }}
      >
        {field.description && (
          <Typography component="p" variant="subtitle1" sx={{ mb: '8px' }}>
            {field.description}
          </Typography>
        )}
        <TextFieldWithHelp
          name={field.key}
          label={field.label}
          value={localValue}
          onChange={handleChange}
          disabled={disabled}
          error={!!error}
          errorText={error?.[0]}
          onReset={handleReset}
          lang={lang}
        />
      </Box>
    );
  }
);

const RichTextFieldRenderer = memo(
  ({
    field,
    value,
    onChange,
    disabled,
    error,
    isFirst = false,
    lang,
  }: {
    field: TextFieldConfig;
    value: string;
    onChange: (value: string) => void;
    disabled: boolean;
    error?: string[];
    isFirst?: boolean;
    lang?: string;
  }) => {
    const theme = useTheme();

    const { t } = useTranslation();
    const isDynamicField = field.category === 'dynamic';
    const fieldId = `rich-text-${field.key}`;
    const labelId = `${fieldId}-label`;
    const errorId = `${fieldId}-error`;
    const descriptionId = `${fieldId}-description`;

    return (
      <Box
        sx={{
          mt: isFirst ? '0px' : field.description ? '40px' : '16px',
          mb: '16px',
        }}
      >
        {field.description && (
          <Typography
            component="p"
            variant="subtitle1"
            sx={{ mb: '8px' }}
            id={descriptionId}
          >
            {field.description}
          </Typography>
        )}
        {isDynamicField && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="body2"
              component="label"
              id={labelId}
              sx={{
                fontWeight: 500,
                color: error
                  ? 'var(--color-dark-grey-red)'
                  : 'var(--color-very-dark-greyish-blue)',
                display: 'block',
              }}
            >
              {field.label}
              {disabled &&
                ` (${t('common.readOnly', { defaultValue: 'Read only' })})`}
            </Typography>
          </Box>
        )}
        <RichTextEditorField
          id={fieldId}
          value={value}
          onChange={onChange}
          disabled={disabled}
          lang={lang}
          error={!!error}
          showLists={field.key !== 'search_title'}
          aria-labelledby={
            isDynamicField
              ? labelId
              : field.description
                ? descriptionId
                : undefined
          }
          aria-label={
            !isDynamicField && !field.description ? field.label : undefined
          }
          aria-required={
            field.key === 'search_title' || field.key === 'instructions'
          }
          aria-describedby={error ? errorId : undefined}
          sx={{
            mt: field.description ? '8px' : '16px',
            mb: '16px',
            ...(field.key === 'additional_info' && {
              '& .MuiTiptap-RichTextContent-root': {
                minHeight: `calc(2 * 1rem * 1.6 + ${theme.spacing(4)}) !important`,
              },
            }),
          }}
        />
        {error && (
          <Typography
            variant="subtitle2"
            id={errorId}
            role="alert"
            aria-live="polite"
            sx={{ mt: 1, display: 'block' }}
          >
            {error[0]}
          </Typography>
        )}
      </Box>
    );
  }
);

const FieldRenderer = memo(
  ({
    field,
    value,
    onChange,
    disabled,
    error,
    isFirst = false,
    lang,
  }: {
    field: TextFieldConfig;
    value: string;
    onChange: (value: string) => void;
    disabled: boolean;
    error?: string[];
    isFirst?: boolean;
    lang?: string;
  }) =>
    field.type === 'text' ? (
      <TextFieldRenderer
        field={field}
        value={value}
        onChange={onChange}
        disabled={disabled}
        error={error}
        isFirst={isFirst}
        lang={lang}
      />
    ) : (
      <RichTextFieldRenderer
        field={field}
        value={value}
        onChange={onChange}
        disabled={disabled}
        error={error}
        isFirst={isFirst}
        lang={lang}
      />
    )
);

const useTextFields = (i18nData: any, currentLocale: string, t: any) => {
  const { branding } = useBranding();
  const hasQualityList = hasExtension(branding, 'quality_lists');

  const dynamicTextFields = useMemo(() => {
    if (!i18nData || !i18nData[currentLocale]) return [];
    const currentLocaleData = i18nData[currentLocale];
    const dynamicFields: TextFieldConfig[] = [];
    Object.keys(currentLocaleData).forEach((key) => {
      const value = currentLocaleData[key];
      if (
        HARDCODED_KEYS.has(key) ||
        typeof value !== 'string' ||
        SKIP_KEYS.includes(key) ||
        (key === 'quality_title' && !hasQualityList)
      )
        return;
      dynamicFields.push({
        key,
        label: formatKeyToLabel(key),
        description: '',
        placeholder: t('branding.richTextEditor.enterPlaceholder', {
          field: key.replace(/_/g, ' '),
        }),
        type: 'text' as const,
        category: 'dynamic' as const,
      });
    });
    return dynamicFields;
  }, [i18nData, currentLocale, t, hasQualityList]);

  const richTextFields = useMemo(() => getRichTextFields(t), [t]);
  return useMemo(
    () => [...richTextFields, ...dynamicTextFields],
    [richTextFields, dynamicTextFields]
  );
};

const useFieldError = (fieldErrors: any, currentLocale: string) => {
  return useCallback(
    (key: string) => {
      const localeErrors = fieldErrors[currentLocale];
      if (
        localeErrors &&
        typeof localeErrors === 'object' &&
        !Array.isArray(localeErrors)
      ) {
        return (localeErrors as Record<string, string[]>)[key];
      }
      return undefined;
    },
    [fieldErrors, currentLocale]
  );
};

const TextTab = memo(
  ({
    currentLocale,
    i18nData,
    onI18nDataChange,
    fieldErrors = {},
  }: I18nTabProps) => {
    const { t } = useTranslation();
    const { impersonationLock } = useAuthContext();

    const allFields = useTextFields(i18nData, currentLocale, t);
    const getFieldError = useFieldError(fieldErrors, currentLocale);

    const handleChange = useCallback(
      (key: string, value: string) => {
        const normalizedValue = normalizeValue(value);
        const currentValue = getI18nValue(
          i18nData,
          currentLocale,
          key
        ) as string;
        if (currentValue !== normalizedValue) {
          const updatedData = updateI18nValue(
            i18nData,
            currentLocale,
            key,
            normalizedValue
          );
          onI18nDataChange(updatedData);
        }
      },
      [i18nData, currentLocale, onI18nDataChange]
    );

    const getValue = useCallback(
      (key: string) => {
        const value = getI18nValue(i18nData, currentLocale, key) as string;
        return value || '';
      },
      [i18nData, currentLocale]
    );

    const searchFields = allFields.filter(
      (field) => field.category === 'search'
    );
    const contentFields = allFields.filter(
      (field) => field.category === 'content'
    );
    const navigationFields = allFields.filter(
      (field) => field.category === 'navigation'
    );
    const dynamicFields = allFields.filter(
      (field) => field.category === 'dynamic'
    );

    return (
      <Box sx={{ p: { xs: 4, sm: 6, md: 8 }, maxWidth: 1600, mx: 'auto' }}>
        <Typography
          component="p"
          variant="body1"
          sx={{
            position: 'absolute',
            left: '-10000px',
          }}
          aria-hidden
        >
          {t('branding.sections.text')}{' '}
          {t('branding.richTextEditor.configuration')}
        </Typography>
        <Grid container spacing={{ xs: 3, md: 4, lg: 6 }}>
          <Grid item xs={12} xl={6}>
            <Box
              sx={{
                width: { xs: '100%', md: '90%', lg: '85%' },
                pr: { xs: 0, md: 2, lg: 3 },
                mt: 32,
              }}
            >
              {searchFields.length > 0 && (
                <>
                  <Typography component="h2" variant="h5" gutterBottom>
                    {t('branding.sections.searchInterface')}
                  </Typography>
                  <Box sx={{ mb: 6 }}>
                    {searchFields.map((field, index) => (
                      <FieldRenderer
                        key={field.key}
                        field={field}
                        value={getValue(field.key)}
                        onChange={(value) => handleChange(field.key, value)}
                        disabled={impersonationLock}
                        error={getFieldError(field.key)}
                        isFirst={index === 0}
                        lang={currentLocale}
                      />
                    ))}
                  </Box>
                </>
              )}

              {navigationFields.length > 0 && (
                <>
                  <Typography component="h2" variant="h5" gutterBottom>
                    {t('branding.sections.navigationMenus')}
                  </Typography>
                  <Box sx={{ mb: 6 }}>
                    {navigationFields.map((field, index) => (
                      <FieldRenderer
                        key={field.key}
                        field={field}
                        value={getValue(field.key)}
                        onChange={(value) => handleChange(field.key, value)}
                        disabled={impersonationLock}
                        error={getFieldError(field.key)}
                        isFirst={index === 0}
                        lang={currentLocale}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} xl={6}>
            <Box
              sx={{
                width: { xs: '100%', md: '90%', lg: '85%' },
                pl: { xs: 0, md: 2, lg: 3 },
                mt: 32,
              }}
            >
              {contentFields.length > 0 && (
                <>
                  <Typography component="h2" variant="h5" gutterBottom>
                    {t('branding.sections.contentInformation')}
                  </Typography>
                  <Box>
                    {contentFields.map((field, index) => (
                      <FieldRenderer
                        key={field.key}
                        field={field}
                        value={getValue(field.key)}
                        onChange={(value) => handleChange(field.key, value)}
                        disabled={impersonationLock}
                        error={getFieldError(field.key)}
                        isFirst={index === 0}
                        lang={currentLocale}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Box>
            <Box
              sx={{
                width: { xs: '100%', md: '90%', lg: '85%' },
                pl: { xs: 0, md: 2, lg: 3 },
                mt: 32,
              }}
            >
              {dynamicFields.length > 0 && (
                <>
                  <Typography component="h2" variant="h5" gutterBottom>
                    {t('branding.sections.additionalSettings')}
                  </Typography>
                  <Box>
                    {dynamicFields.map((field, index) => (
                      <FieldRenderer
                        key={field.key}
                        field={field}
                        value={getValue(field.key)}
                        onChange={(value) => handleChange(field.key, value)}
                        disabled={impersonationLock}
                        error={getFieldError(field.key)}
                        isFirst={index === 0}
                        lang={currentLocale}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }
);

TextTab.displayName = 'TextTab';
TextFieldRenderer.displayName = 'TextFieldRenderer';
RichTextFieldRenderer.displayName = 'RichTextFieldRenderer';
FieldRenderer.displayName = 'FieldRenderer';

export default TextTab;
